import React, { useState, useEffect, JSX } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Checkbox,
  FormControlLabel,
  Grid,
  IconButton,
  Link,
} from '@mui/material';
import {
  Email as EmailIcon,
  Security as SecurityIcon,
  CheckCircle as CheckCircleIcon,
  ErrorOutline as ErrorIcon,
  Refresh as RefreshIcon,
  DevicesOther as DevicesIcon,
  VisibilityOff,
  Visibility,
  LockReset as LockResetIcon,
} from '@mui/icons-material';
import { useAuth } from 'hooks/AuthContext';
import { BackstoryPageProps } from './BackstoryTab';
import { useNavigate } from 'react-router-dom';
import { MFAData } from 'types/types';
import { validatePasswordStrength } from 'domain/auth-rules';

// Email Verification Component
const EmailVerificationPage = (_props: BackstoryPageProps): JSX.Element => {
  const { verifyEmail, resendEmailVerification, getPendingVerificationEmail, isLoading, error } =
    useAuth();
  const navigate = useNavigate();
  const [status, setStatus] = useState<'pending' | 'success' | 'error'>('pending');
  const [message, setMessage] = useState('');
  const [userType, setUserType] = useState<string>('');

  useEffect(() => {
    // Get token from URL parameters
    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get('token');

    const handleVerifyEmail = async (token: string): Promise<void> => {
      if (!token) {
        setStatus('error');
        setMessage('Invalid verification link');
        return;
      }

      try {
        const result = await verifyEmail({ token });

        if (result) {
          setStatus('success');
          setMessage(result.message);
          setUserType(result.userType);

          // Redirect to login after 3 seconds
          setTimeout(() => {
            navigate('/login');
          }, 3000);
        } else {
          setStatus('error');
          setMessage('Email verification failed');
        }
      } catch (error) {
        setStatus('error');
        setMessage('Email verification failed');
      }
    };

    if (token) {
      handleVerifyEmail(token);
    }
  }, [navigate, verifyEmail]);

  const handleResendVerification = async (): Promise<void> => {
    const email = getPendingVerificationEmail();
    if (!email) {
      setMessage('No pending verification email found.');
      return;
    }

    try {
      const success = await resendEmailVerification(email);
      if (success) {
        setMessage('Verification email sent successfully!');
      }
    } catch (error) {
      setMessage('Failed to resend verification email.');
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: 'grey.50',
        p: 2,
      }}
    >
      <Card sx={{ maxWidth: 500, width: '100%' }}>
        <CardContent sx={{ p: 4 }}>
          <Box textAlign="center" mb={3}>
            {status === 'pending' && (
              <>
                <EmailIcon sx={{ fontSize: 64, color: 'primary.main', mb: 2 }} />
                <Typography variant="h4" gutterBottom>
                  Verifying Email
                </Typography>
                <Typography color="text.secondary">
                  Please wait while we verify your email address...
                </Typography>
              </>
            )}

            {status === 'success' && (
              <>
                <CheckCircleIcon sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
                <Typography variant="h4" gutterBottom color="success.main">
                  Email Verified!
                </Typography>
                <Typography color="text.secondary">
                  Your {userType} account has been successfully activated.
                </Typography>
              </>
            )}

            {status === 'error' && (
              <>
                <ErrorIcon sx={{ fontSize: 64, color: 'error.main', mb: 2 }} />
                <Typography variant="h4" gutterBottom color="error.main">
                  Verification Failed
                </Typography>
                <Typography color="text.secondary">
                  We couldn&apos;t verify your email address.
                </Typography>
              </>
            )}
          </Box>

          {isLoading && (
            <Box display="flex" justifyContent="center" my={3}>
              <CircularProgress />
            </Box>
          )}

          {(message || error) && (
            <Alert
              severity={status === 'success' ? 'success' : status === 'error' ? 'error' : 'info'}
              sx={{ mt: 2 }}
            >
              {message || error}
            </Alert>
          )}

          {status === 'success' && (
            <Box mt={3} textAlign="center">
              <Typography variant="body2" color="text.secondary" mb={2}>
                You will be redirected to the login page in a few seconds...
              </Typography>
              <Button
                variant="contained"
                onClick={(): void => {
                  navigate('/login');
                }}
                fullWidth
              >
                Go to Login
              </Button>
            </Box>
          )}

          {status === 'error' && (
            <Box mt={3}>
              <Button
                variant="outlined"
                onClick={handleResendVerification}
                disabled={isLoading}
                startIcon={<RefreshIcon />}
                fullWidth
                sx={{ mb: 2 }}
              >
                Resend Verification Email
              </Button>
              <Button
                variant="contained"
                onClick={(): void => {
                  navigate('/login');
                }}
                fullWidth
              >
                Back to Login
              </Button>
            </Box>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

// MFA Verification Component
interface MFAVerificationDialogProps {
  open: boolean;
  onClose: () => void;
  onVerificationSuccess: () => void;
}
const MFAVerificationDialog = (props: MFAVerificationDialogProps): JSX.Element => {
  const { open, onClose, onVerificationSuccess } = props;
  const { verifyMFA, resendMFACode, clearMFA, mfaResponse, isLoading, error } = useAuth();
  const [code, setCode] = useState('');
  const [rememberDevice, setRememberDevice] = useState(false);
  const [localError, setLocalError] = useState('');
  const [timeLeft, setTimeLeft] = useState(600); // 10 minutes in seconds
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    if (!error) {
      return;
    }
    try {
      const jsonStr = error.replace(/^[^{]*/, '');
      const data = JSON.parse(jsonStr);
      setErrorMessage(data.error.message);
    } catch {
      setErrorMessage('An unexpected error occurred. Please try again.');
    }
  }, [error]);

  useEffect(() => {
    if (!open) return;

    const timer = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) {
          clearInterval(timer);
          setLocalError('MFA code has expired. Please try logging in again.');
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [open]);

  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleVerifyMFA = async (): Promise<void> => {
    if (!code || code.length !== 6) {
      setLocalError('Please enter a valid 6-digit code');
      return;
    }

    if (!mfaResponse || !mfaResponse.mfaData) {
      setLocalError('MFA data not available');
      return;
    }

    setLocalError('');

    try {
      const success = await verifyMFA({
        email: mfaResponse.mfaData.email,
        code,
        deviceId: mfaResponse.mfaData.deviceId,
        rememberDevice,
      });

      if (success) {
        onVerificationSuccess();
        onClose();
      }
    } catch (error) {
      setLocalError('Verification failed. Please try again.');
    }
  };

  const handleResendCode = async (): Promise<void> => {
    if (!mfaResponse || !mfaResponse.mfaData) {
      setLocalError('MFA data not available');
      return;
    }

    try {
      const success = await resendMFACode(
        mfaResponse.mfaData.email,
        mfaResponse.mfaData.deviceId,
        mfaResponse.mfaData.deviceName
      );
      if (success) {
        setTimeLeft(600); // Reset timer
        setLocalError('');
        alert('New verification code sent to your email');
      }
    } catch (error) {
      setLocalError('Failed to resend code');
    }
  };

  const handleClose = (): void => {
    clearMFA();
    onClose();
  };

  if (!mfaResponse || !mfaResponse.mfaData) return <></>;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <SecurityIcon color="primary" />
          <Typography variant="h6">Verify Your Identity</Typography>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Alert severity="info" sx={{ mb: 3 }}>
          We&apos;ve detected a login from a new device:{' '}
          <strong>{mfaResponse.mfaData.deviceName}</strong>
        </Alert>

        <Typography variant="body1" gutterBottom>
          We&apos;ve sent a 6-digit verification code to:
        </Typography>
        <Typography variant="h6" color="primary" gutterBottom>
          {mfaResponse.mfaData.email}
        </Typography>

        <TextField
          fullWidth
          label="Enter 6-digit code"
          value={code}
          onChange={(e): void => {
            const value = e.target.value.replace(/\D/g, '').slice(0, 6);
            setCode(value);
            setLocalError('');
          }}
          placeholder="000000"
          inputProps={{
            maxLength: 6,
            style: {
              fontSize: 24,
              textAlign: 'center',
              letterSpacing: 8,
            },
          }}
          sx={{ mt: 2, mb: 2 }}
          error={!!(localError || errorMessage)}
          helperText={localError || errorMessage}
        />

        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="body2" color="text.secondary">
            Code expires in: {formatTime(timeLeft)}
          </Typography>
          <Button
            size="small"
            onClick={handleResendCode}
            disabled={isLoading || timeLeft > 540} // Allow resend after 1 minute
          >
            Resend Code
          </Button>
        </Box>

        <FormControlLabel
          control={
            <Checkbox
              checked={rememberDevice}
              onChange={(e): void => {
                setRememberDevice(e.target.checked);
              }}
            />
          }
          label="Remember this device for 90 days"
        />

        <Alert severity="warning" sx={{ mt: 2 }}>
          <Typography variant="body2">
            If you didn&apos;t attempt to log in, please change your password immediately.
          </Typography>
        </Alert>
      </DialogContent>

      <DialogActions sx={{ p: 3 }}>
        <Button onClick={handleClose} disabled={isLoading}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleVerifyMFA}
          disabled={isLoading || !code || code.length !== 6 || timeLeft === 0}
        >
          {isLoading ? <CircularProgress size={20} /> : 'Verify'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// Enhanced Registration Success Component
const RegistrationSuccessDialog = ({
  open,
  onClose,
  email,
  userType,
}: {
  open: boolean;
  onClose: () => void;
  email: string;
  userType: string;
}): JSX.Element => {
  const { resendEmailVerification, isLoading } = useAuth();
  const [resendMessage, setResendMessage] = useState('');

  const handleResendVerification = async (): Promise<void> => {
    try {
      const success = await resendEmailVerification(email);
      if (success) {
        setResendMessage('Verification email sent!');
      }
    } catch (error: unknown) {
      const tmp = error as { message?: string };
      setResendMessage(tmp?.message || 'Network error. Please try again.');
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogContent sx={{ textAlign: 'center', p: 4 }}>
        <EmailIcon sx={{ fontSize: 64, color: 'primary.main', mb: 2 }} />

        <Typography variant="h5" gutterBottom>
          Check Your Email
        </Typography>

        <Typography variant="body1" color="text.secondary" paragraph>
          We&apos;e sent a verification link to:
        </Typography>

        <Typography variant="h6" color="primary" gutterBottom>
          {email}
        </Typography>

        <Alert severity="info" sx={{ mt: 2, mb: 3, textAlign: 'left' }}>
          <Typography variant="body2">
            <strong>Next steps:</strong>
            <br />
            1. Check your email inbox (and spam folder)
            <br />
            2. Click the verification link
            <br />
            3. Your {userType} account will be activated
          </Typography>
        </Alert>

        {resendMessage && (
          <Alert severity={resendMessage.includes('sent') ? 'success' : 'error'} sx={{ mb: 2 }}>
            {resendMessage}
          </Alert>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 3, justifyContent: 'space-between' }}>
        <Button
          onClick={handleResendVerification}
          disabled={isLoading}
          startIcon={isLoading ? <CircularProgress size={16} /> : <RefreshIcon />}
        >
          Resend Email
        </Button>
        <Button variant="contained" onClick={onClose}>
          Got It
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// Enhanced Login Component with MFA Support
const LoginForm = (): JSX.Element => {
  const { login, mfaResponse, isLoading, error, user } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);

  useEffect(() => {
    if (!error) {
      return;
    }
    try {
      const jsonStr = error.replace(/^[^{]*/, '');
      const data = JSON.parse(jsonStr);
      setErrorMessage(data.error.message);
    } catch {
      setErrorMessage('An unexpected error occurred. Please try again.');
    }
  }, [error]);

  const handleLogin = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();

    const success = await login({
      login: email,
      password,
    });

    console.log(`login success: ${success}`);
    if (success) {
      // Redirect based on user type - this could be handled in AuthContext
      // or by a higher-level component that listens to auth state changes
      handleLoginSuccess();
    }
  };

  const handleMFASuccess = (): void => {
    handleLoginSuccess();
  };

  const handleLoginSuccess = (): void => {
    if (!user) {
      navigate('/');
    } else {
      navigate(`/${user.userType}/dashboard`);
    }
    console.log('Login successful - redirect to dashboard');
  };

  if (showForgotPassword) {
    return <ForgotPasswordForm onBack={(): void => setShowForgotPassword(false)} />;
  }

  return (
    <Box component="form" onSubmit={handleLogin} sx={{ mt: 1 }}>
      <TextField
        margin="normal"
        required
        fullWidth
        label="Email or Username"
        value={email}
        onChange={(e): void => {
          setEmail(e.target.value);
        }}
        autoComplete="email"
        autoFocus
      />
      <TextField
        fullWidth
        label="Password"
        type={showPassword ? 'text' : 'password'}
        value={password}
        onChange={(e): void => {
          setPassword(e.target.value);
        }}
        autoComplete="current-password"
        placeholder="Create a strong password"
        required
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                aria-label="toggle password visibility"
                onClick={(): void => {
                  setShowPassword(!showPassword);
                }}
                onMouseDown={(e): void => {
                  e.preventDefault();
                }}
                edge="end"
              >
                {showPassword ? <VisibilityOff /> : <Visibility />}
              </IconButton>
            </InputAdornment>
          ),
        }}
      />

      <Box textAlign="right" sx={{ mt: 0.5 }}>
        <Link
          component="button"
          variant="body2"
          onClick={(): void => setShowForgotPassword(true)}
          underline="hover"
          type="button"
        >
          Forgot password?
        </Link>
      </Box>

      {errorMessage && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {errorMessage}
        </Alert>
      )}

      <Button
        type="submit"
        fullWidth
        variant="contained"
        disabled={isLoading}
        sx={{ mt: 3, mb: 2 }}
      >
        {isLoading ? <CircularProgress size={20} /> : 'Sign In'}
      </Button>

      {/* MFA Dialog */}
      <MFAVerificationDialog
        open={mfaResponse?.mfaRequired || false}
        onClose={(): void => {
          console.log();
        }} // This will be handled by clearMFA in the dialog
        onVerificationSuccess={handleMFASuccess}
      />
    </Box>
  );
};

// Device Management Component
const TrustedDevicesManager = (): JSX.Element => {
  const devices: MFAData[] = [];

  return (
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          <DevicesIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
          Trusted Devices
        </Typography>

        <Typography variant="body2" color="text.secondary" paragraph>
          Manage devices that you&apos;ve marked as trusted. You won&apos;t need to verify your
          identity when signing in from these devices.
        </Typography>

        {devices.length === 0 ? (
          <Alert severity="info">
            No trusted devices yet. When you log in from a new device and choose to remember it, it
            will appear here.
          </Alert>
        ) : (
          <Grid container spacing={2}>
            {devices.map((device, index) => (
              <Grid key={index} size={{ xs: 12, md: 6 }}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="subtitle1">{device.deviceName}</Typography>
                    {/* <Typography variant="body2" color="text.secondary">
                      Added: {new Date(device.addedAt).toLocaleDateString()}
                    </Typography> */}
                    {/* <Typography variant="body2" color="text.secondary">
                      Last used: {new Date(device.lastUsed).toLocaleDateString()}
                    </Typography> */}
                    <Button
                      size="small"
                      color="error"
                      sx={{ mt: 1 }}
                      onClick={(): void => {
                        console.log('Remove device');
                      }}
                    >
                      Remove
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
      </CardContent>
    </Card>
  );
};

// Forgot Password Form — shown inside LoginPage when user clicks "Forgot password?"
const ForgotPasswordForm = ({ onBack }: { onBack: () => void }): JSX.Element => {
  const { requestPasswordReset, isLoading } = useAuth();
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    setError(null);
    const ok = await requestPasswordReset(email);
    if (ok) {
      setSubmitted(true);
    } else {
      setError('Something went wrong. Please try again.');
    }
  };

  if (submitted) {
    return (
      <Box textAlign="center" sx={{ mt: 2 }}>
        <CheckCircleIcon sx={{ fontSize: 56, color: 'success.main', mb: 1 }} />
        <Typography variant="h6" gutterBottom>
          Check your email
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          If an account with <strong>{email}</strong> exists, you will receive a password reset link
          shortly.
        </Typography>
        <Button variant="outlined" onClick={onBack} fullWidth>
          Back to Sign In
        </Button>
      </Box>
    );
  }

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1 }}>
      <Box display="flex" alignItems="center" gap={1} mb={2}>
        <LockResetIcon color="primary" />
        <Typography variant="h6">Reset your password</Typography>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Enter the email address associated with your account and we&apos;ll send you a reset link.
      </Typography>
      <TextField
        margin="normal"
        required
        fullWidth
        label="Email address"
        type="email"
        value={email}
        onChange={(e): void => setEmail(e.target.value)}
        autoComplete="email"
        autoFocus
      />
      {error && (
        <Alert severity="error" sx={{ mt: 1 }}>
          {error}
        </Alert>
      )}
      <Button
        type="submit"
        fullWidth
        variant="contained"
        disabled={isLoading || !email}
        sx={{ mt: 3, mb: 2 }}
      >
        {isLoading ? <CircularProgress size={20} /> : 'Send reset link'}
      </Button>
      <Box textAlign="center">
        <Link component="button" variant="body2" onClick={onBack} underline="hover">
          Back to Sign In
        </Link>
      </Box>
    </Box>
  );
};

// Reset Password Page — visited via the link in the reset email (/login/reset-password?token=…)
const ResetPasswordPage = (_props: BackstoryPageProps): JSX.Element => {
  const { confirmPasswordReset, isLoading } = useAuth();
  const navigate = useNavigate();
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [status, setStatus] = useState<'form' | 'success' | 'error'>('form');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [token, setToken] = useState<string | null>(null);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const t = params.get('token');
    if (!t) {
      setStatus('error');
      setErrorMessage('Invalid or missing reset link. Please request a new one.');
    } else {
      setToken(t);
    }
  }, []);

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    setErrorMessage(null);

    if (newPassword !== confirmPassword) {
      setErrorMessage('Passwords do not match.');
      return;
    }
    if (!token) {
      setErrorMessage('Missing reset token.');
      return;
    }

    // Enforce the same password rules as the backend before submitting, so the
    // user gets actionable feedback in the form instead of an opaque failure.
    const strength = validatePasswordStrength(newPassword);
    if (!strength.isValid) {
      setErrorMessage(strength.issues.join('. '));
      return;
    }

    const result = await confirmPasswordReset(token, newPassword);
    if (result.ok) {
      setStatus('success');
    } else if (result.tokenInvalid) {
      // The link itself is bad — there's no point keeping the form open.
      setStatus('error');
      setErrorMessage('The reset link is invalid or has expired. Please request a new one.');
    } else {
      // A correctable failure (e.g. password rejected by the server). Keep the
      // form open and show why so the user can try a different password.
      setErrorMessage(result.error ?? 'Password reset failed. Please try again.');
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: 'grey.50',
        p: 2,
      }}
    >
      <Card sx={{ maxWidth: 480, width: '100%' }}>
        <CardContent sx={{ p: 4 }}>
          {status === 'success' && (
            <Box textAlign="center">
              <CheckCircleIcon sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
              <Typography variant="h5" gutterBottom color="success.main">
                Password updated!
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Your password has been changed. You can now sign in with your new password.
              </Typography>
              <Button
                variant="contained"
                fullWidth
                onClick={(): void => {
                  navigate('/login');
                }}
              >
                Go to Sign In
              </Button>
            </Box>
          )}

          {status === 'error' && (
            <Box textAlign="center">
              <ErrorIcon sx={{ fontSize: 64, color: 'error.main', mb: 2 }} />
              <Typography variant="h5" gutterBottom color="error.main">
                Link invalid or expired
              </Typography>
              {errorMessage && (
                <Alert severity="error" sx={{ mt: 1, mb: 3 }}>
                  {errorMessage}
                </Alert>
              )}
              <Button
                variant="outlined"
                fullWidth
                onClick={(): void => {
                  navigate('/login');
                }}
                sx={{ mb: 1 }}
              >
                Back to Sign In
              </Button>
            </Box>
          )}

          {status === 'form' && (
            <>
              <Box display="flex" alignItems="center" gap={1} mb={2}>
                <LockResetIcon color="primary" sx={{ fontSize: 32 }} />
                <Typography variant="h5">Set new password</Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Choose a strong password for your account.
              </Typography>
              <Box component="form" onSubmit={handleSubmit}>
                <TextField
                  fullWidth
                  margin="normal"
                  label="New password"
                  type={showPassword ? 'text' : 'password'}
                  value={newPassword}
                  onChange={(e): void => setNewPassword(e.target.value)}
                  required
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={(): void => setShowPassword(v => !v)}
                          onMouseDown={(e): void => e.preventDefault()}
                          edge="end"
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
                <TextField
                  fullWidth
                  margin="normal"
                  label="Confirm new password"
                  type={showPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e): void => setConfirmPassword(e.target.value)}
                  required
                  error={confirmPassword.length > 0 && newPassword !== confirmPassword}
                  helperText={
                    confirmPassword.length > 0 && newPassword !== confirmPassword
                      ? 'Passwords do not match'
                      : ''
                  }
                />
                {errorMessage && (
                  <Alert severity="error" sx={{ mt: 1 }}>
                    {errorMessage}
                  </Alert>
                )}
                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  disabled={isLoading || !newPassword || !confirmPassword}
                  sx={{ mt: 3, mb: 2 }}
                >
                  {isLoading ? <CircularProgress size={20} /> : 'Update password'}
                </Button>
                <Box textAlign="center">
                  <Link
                    component="button"
                    variant="body2"
                    onClick={(): void => {
                      navigate('/login');
                    }}
                    underline="hover"
                  >
                    Back to Sign In
                  </Link>
                </Box>
              </Box>
            </>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export {
  EmailVerificationPage,
  MFAVerificationDialog,
  TrustedDevicesManager,
  RegistrationSuccessDialog,
  LoginForm,
  ForgotPasswordForm,
  ResetPasswordPage,
};
